import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Tab,
  Tabs,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  PersonAdd as InviteIcon,
  SwapHoriz as TransferIcon,
} from "@mui/icons-material";
import {
  fetchOrgDetails,
  fetchMembers,
  fetchInvitations,
  updateOrg,
  changeMemberRole,
  removeMemberFromOrg,
  inviteMember,
  revokeOrgInvitation,
  transferOrgOwnership,
  createOrg,
} from "../redux/slices/orgSlice";

const ROLE_COLORS = {
  OWNER: "error",
  ADMIN: "warning",
  DEVELOPER: "primary",
  VIEWER: "default",
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ py: 3 }}>{children}</Box> : null;
}

export default function OrgSettings() {
  const dispatch = useDispatch();
  const { currentOrg, orgDetails, members, invitations, loading, error } =
    useSelector((state) => state.org);
  const user = useSelector((state) => state.user.user);
  const [tab, setTab] = useState(0);
  const [orgName, setOrgName] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("DEVELOPER");
  const [createOpen, setCreateOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const orgId = currentOrg?.id;
  const myRole = currentOrg?.role;
  const canManageMembers = myRole === "OWNER" || myRole === "ADMIN";
  const isOwner = myRole === "OWNER";

  useEffect(() => {
    if (orgId) {
      dispatch(fetchOrgDetails(orgId));
      dispatch(fetchMembers(orgId));
      if (canManageMembers) dispatch(fetchInvitations(orgId));
    }
  }, [dispatch, orgId, canManageMembers]);

  useEffect(() => {
    if (orgDetails) setOrgName(orgDetails.name);
  }, [orgDetails]);

  // Check URL for ?create=true
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("create") === "true") setCreateOpen(true);
  }, []);

  const handleUpdateOrg = async () => {
    if (!orgName.trim()) return;
    try {
      await dispatch(
        updateOrg({ orgId, data: { name: orgName.trim() } }),
      ).unwrap();
      setFeedback({ type: "success", message: "Organization updated" });
    } catch (err) {
      setFeedback({
        type: "error",
        message: err.error || err.message || "Update failed",
      });
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await dispatch(
        inviteMember({ orgId, email: inviteEmail.trim(), role: inviteRole }),
      ).unwrap();
      setInviteOpen(false);
      setInviteEmail("");
      setFeedback({ type: "success", message: "Invitation sent" });
    } catch (err) {
      setFeedback({
        type: "error",
        message: err.error || err.message || "Invite failed",
      });
    }
  };

  const handleRoleChange = async (memberId, role) => {
    try {
      await dispatch(changeMemberRole({ orgId, memberId, role })).unwrap();
    } catch (err) {
      setFeedback({ type: "error", message: err.error || err.message });
    }
  };

  const handleRemoveMember = async (memberId) => {
    setConfirmDialog({
      open: true,
      title: "Remove Member",
      message:
        "Are you sure you want to remove this member from the organization?",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await dispatch(removeMemberFromOrg({ orgId, memberId })).unwrap();
        } catch (err) {
          setFeedback({ type: "error", message: err.error || err.message });
        }
      },
    });
  };

  const handleRevokeInvite = async (invitationId) => {
    try {
      await dispatch(revokeOrgInvitation({ orgId, invitationId })).unwrap();
    } catch (err) {
      setFeedback({ type: "error", message: err.error || err.message });
    }
  };

  const handleTransfer = async (memberId) => {
    setConfirmDialog({
      open: true,
      title: "Transfer Ownership",
      message:
        "Are you sure you want to transfer ownership? You will become an Admin.",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await dispatch(transferOrgOwnership({ orgId, memberId })).unwrap();
          setFeedback({ type: "success", message: "Ownership transferred" });
        } catch (err) {
          setFeedback({ type: "error", message: err.error || err.message });
        }
      },
    });
  };

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) return;
    try {
      await dispatch(createOrg({ name: newOrgName.trim() })).unwrap();
      setCreateOpen(false);
      setNewOrgName("");
      setFeedback({ type: "success", message: "Organization created" });
    } catch (err) {
      setFeedback({
        type: "error",
        message: err.error || err.message || "Create failed",
      });
    }
  };

  if (!orgId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No organization selected.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Organization Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage your organization, members, and invitations.
      </Typography>

      {feedback && (
        <Alert
          severity={feedback.type}
          onClose={() => setFeedback(null)}
          sx={{ mb: 2 }}
        >
          {feedback.message}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="General" />
        <Tab label="Members" />
        {canManageMembers && <Tab label="Invitations" />}
      </Tabs>

      {/* General Tab */}
      <TabPanel value={tab} index={0}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Organization Name
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <TextField
              size="small"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              disabled={!isOwner && myRole !== "ADMIN"}
              sx={{ flex: 1, maxWidth: 400 }}
            />
            <Button
              variant="contained"
              onClick={handleUpdateOrg}
              disabled={!isOwner && myRole !== "ADMIN"}
            >
              Save
            </Button>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Slug: <strong>{orgDetails?.slug}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Plan: <strong>{orgDetails?.plan?.name || "Free"}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sites: <strong>{orgDetails?._count?.sites || 0}</strong> · Apps:{" "}
              <strong>{orgDetails?._count?.applications || 0}</strong>
            </Typography>
          </Box>
        </Paper>
      </TabPanel>

      {/* Members Tab */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Members ({members.length})
          </Typography>
          {canManageMembers && (
            <Button
              startIcon={<InviteIcon />}
              variant="outlined"
              size="small"
              onClick={() => setInviteOpen(true)}
            >
              Invite Member
            </Button>
          )}
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        sx={{ width: 28, height: 28, fontSize: "0.75rem" }}
                      >
                        {member.user?.firstName?.[0] ||
                          member.user?.email?.[0]?.toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {member.user?.firstName} {member.user?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {member.user?.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {canManageMembers &&
                    member.role !== "OWNER" &&
                    member.user?.id !== user?.id ? (
                      <Select
                        size="small"
                        value={member.role}
                        onChange={(e) =>
                          handleRoleChange(member.id, e.target.value)
                        }
                        sx={{ fontSize: "0.8125rem", minWidth: 120 }}
                      >
                        <MenuItem value="ADMIN">Admin</MenuItem>
                        <MenuItem value="DEVELOPER">Developer</MenuItem>
                        <MenuItem value="VIEWER">Viewer</MenuItem>
                      </Select>
                    ) : (
                      <Chip
                        label={member.role}
                        size="small"
                        color={ROLE_COLORS[member.role]}
                        sx={{ textTransform: "capitalize" }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(member.joinedAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {member.role !== "OWNER" &&
                      canManageMembers &&
                      member.user?.id !== user?.id && (
                        <>
                          {isOwner && (
                            <IconButton
                              size="small"
                              onClick={() => handleTransfer(member.id)}
                              title="Transfer ownership"
                            >
                              <TransferIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMember(member.id)}
                            title="Remove"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Invitations Tab */}
      {canManageMembers && (
        <TabPanel value={tab} index={2}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Pending Invitations ({invitations.length})
          </Typography>

          {invitations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No pending invitations.
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Invited By</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={inv.role}
                          size="small"
                          color={ROLE_COLORS[inv.role]}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(inv.expiresAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {inv.inviter?.firstName} {inv.inviter?.lastName}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRevokeInvite(inv.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      )}

      {/* Invite Member Dialog */}
      <Dialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Invite Member</DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <TextField
            label="Email Address"
            fullWidth
            size="small"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Select
            fullWidth
            size="small"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            label="Role"
          >
            <MenuItem value="ADMIN">Admin</MenuItem>
            <MenuItem value="DEVELOPER">Developer</MenuItem>
            <MenuItem value="VIEWER">Viewer</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInvite}>
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Organization Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Organization</DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <TextField
            label="Organization Name"
            fullWidth
            size="small"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateOrg}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Action Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog((prev) => ({ ...prev, open: false }))
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDialog.onConfirm}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
